// packages/widget/src/components/steps/contact-prompt.ts
//
// #592 — ONE renderer for "contact the restaurant directly", so every refusal
// path says it the same way and, crucially, gives the guest something to TAP.
//
// Kayla, Round-15 A1: "We will have to add restaurant contact number there as
// well as email address." She is right: #590/#591 turn large parties away BY
// DESIGN, and those are exactly the bookings the venue wants by phone. Copy
// that says "contact us" without a number is a dead end on a phone screen.
//
// Falls back to the plain sentence when a venue has configured neither — never
// renders an empty "call us on ." fragment.

import { css, html, nothing, type TemplateResult } from 'lit';

/** Phone as a tel: href — strip everything a dialler cannot use, keep a leading +. */
export function telHref(phone: string): string {
  const t = String(phone || '').trim();
  const plus = t.startsWith('+') ? '+' : '';
  return `tel:${plus}${t.replace(/[^0-9]/g, '')}`;
}

/**
 * The DECISION, separated from the markup: what (if anything) can the guest be
 * offered? Pure and string-only so it is unit-testable in this package's
 * node-only test environment — no DOM, no lit render. `contactPrompt` below is
 * a thin template over this.
 *
 * `display` preserves the venue's own formatting; `href` is the normalised
 * machine form. Whitespace-only input counts as ABSENT (an operator clearing
 * the field), so a venue with neither detail gets a clean sentence rather than
 * "contact us on ." or a dangling "or".
 */
export function contactLinks(
  contactPhone?: string,
  contactEmail?: string,
): { phone?: { href: string; display: string }; email?: { href: string; display: string } } {
  const p = String(contactPhone || '').trim();
  const e = String(contactEmail || '').trim();
  return {
    ...(p ? { phone: { href: telHref(p), display: p } } : {}),
    ...(e ? { email: { href: `mailto:${e}`, display: e } } : {}),
  };
}

/**
 * A colon promises a list. With nothing to list, it has to become a full stop.
 *
 * Found live on Creation immediately after the #592 promote: their prompt read
 * "For larger parties, please contact the restaurant directly:" — trailing
 * colon, nothing after it — because they have no contact details set yet. The
 * caller bakes the colon into its lead, which is right when links follow and
 * wrong when they don't, and only THIS function knows which case it is.
 *
 * Exported so it is testable in this package's node-only environment.
 */
export function leadWhenNothingToOffer(lead: string): string {
  return String(lead || '').replace(/\s*:\s*$/, '.');
}

/**
 * The contact line. `lead` is the sentence that precedes it, so each caller
 * keeps its own framing ("For larger parties…" vs "We can't seat a party of 6
 * online.") while the actionable half stays identical everywhere. A lead may
 * end in a colon introducing the links — see `leadWhenNothingToOffer` for what
 * happens to it when there are none.
 */
export function contactPrompt(
  lead: string,
  contactPhone?: string,
  contactEmail?: string,
): TemplateResult {
  const { phone, email } = contactLinks(contactPhone, contactEmail);
  if (!phone && !email) return html`<span>${leadWhenNothingToOffer(lead)}</span>`;
  return html`<span
    >${lead}
    ${phone
      ? html`<a class="yebo-contact-link" href=${phone.href}>${phone.display}</a>`
      : nothing}${phone && email ? html`<span> or </span>` : nothing}${email
      ? html`<a class="yebo-contact-link" href=${email.href}>${email.display}</a>`
      : nothing}</span
  >`;
}

/**
 * Link styling for the contact prompts. Shared so both callers look identical.
 *
 * Round-16 A1/A2 (Simon, twice): "just the colour… the blue which is hard to
 * see." Two causes: this class had NO styles at all, so it fell back to the
 * browser's default blue, and the sibling `.empty-state-cta` used
 * `--yebo-primary` — the VENUE'S brand colour, which cannot be trusted for
 * contrast. Lekker's primary is blue; Creation's is near-black green
 * (#02403A), which on the dark panel would have been close to invisible.
 *
 * So: body text colour (already proven legible against the panel, whatever the
 * venue's palette) plus a persistent underline to carry the link affordance
 * that colour alone was carrying. Brand colour is demoted to hover/focus,
 * where being decorative is fine.
 */
export const contactLinkStyles = css`
  .yebo-contact-link {
    color: var(--yebo-text);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    font-weight: 600;
    /* A phone number must not wrap mid-number. */
    white-space: nowrap;
  }
  .yebo-contact-link:hover,
  .yebo-contact-link:focus-visible {
    color: var(--yebo-primary);
    text-decoration-thickness: 2px;
  }
  .yebo-contact-link:focus-visible {
    outline: 2px solid var(--yebo-primary);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
